import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';
import { useLang } from '../i18n';
import { COOKIE_CONSENT_STORAGE_KEY, getCookieConsent } from '../utils/cookieConsent';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CookieConsent = () => {
  const { t, lang } = useLang();
  const [visible, setVisible] = useState(() => !getCookieConsent());
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [prefs, setPrefs] = useState({ preferences: true, analytics: false, marketing: false });

  useEffect(() => {
    const open = () => {
      const saved = getCookieConsent();
      if (saved?.categories) setPrefs((p) => ({ ...p, ...saved.categories }));
      setPrefsOpen(true);
    };
    window.addEventListener('eco:cookie-preferences', open);
    return () => window.removeEventListener('eco:cookie-preferences', open);
  }, []);

  const save = (categories) => {
    const record = { categories, at: new Date().toISOString() };
    localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(record));
    window.dispatchEvent(new CustomEvent('eco:cookie-consent-changed', { detail: record }));
    fetch(`${API}/consents`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ categories, lang, path: window.location.pathname }),
    }).catch(() => {});
    setVisible(false);
    setPrefsOpen(false);
  };

  const acceptAll = () => save({ preferences: true, analytics: true, marketing: true });
  const rejectOptional = () => save({ preferences: false, analytics: false, marketing: false });

  if (!visible && !prefsOpen) return null;

  const Toggle = ({ id, label, desc, locked }) => (
    <div className="flex items-start justify-between gap-4 rounded-xl bg-eco-cream p-4" data-testid={`cookie-cat-${id}`}>
      <div>
        <p className="text-sm font-semibold text-eco-ink">{label}</p>
        <p className="mt-1 text-xs leading-relaxed text-eco-ink/60">{desc}</p>
      </div>
      {locked ? (
        <span className="shrink-0 rounded-full bg-eco-green/10 px-3 py-1 text-[11px] font-bold text-eco-green" data-testid={`cookie-cat-${id}-locked`}>{id === 'marketing' ? t('cookie.inactive') : t('cookie.always')}</span>
      ) : (
        <button
          type="button"
          role="switch"
          aria-checked={prefs[id]}
          data-testid={`cookie-toggle-${id}`}
          onClick={() => setPrefs((p) => ({ ...p, [id]: !p[id] }))}
          className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${prefs[id] ? 'bg-eco-green' : 'bg-eco-ink/20'}`}
        >
          <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${prefs[id] ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
        </button>
      )}
    </div>
  );

  return (
    <>
      {visible && !prefsOpen && (
        <div data-testid="cookie-banner" className="fixed inset-x-0 bottom-0 z-[80] border-t border-eco-ink/10 bg-white/95 shadow-[0_-8px_30px_rgba(0,0,0,0.08)] backdrop-blur-md">
          <div className="eco-container flex flex-col gap-4 py-5 md:flex-row md:items-center">
            <div className="flex flex-1 items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-eco-cream text-eco-green"><Cookie size={17} /></span>
              <div>
                <p className="text-sm font-semibold text-eco-ink">{t('cookie.title')}</p>
                <p className="mt-1 max-w-2xl text-xs leading-relaxed text-eco-ink/60">{t('cookie.text')} <Link to="/cookies" data-testid="cookie-banner-policy" className="font-medium text-eco-green hover:underline">{t('foot.leg.cookies')}</Link></p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button data-testid="cookie-reject" onClick={rejectOptional} className="eco-btn-secondary !min-h-10 px-4 text-xs">{t('cookie.reject')}</button>
              <button data-testid="cookie-customize" onClick={() => setPrefsOpen(true)} className="eco-btn-secondary !min-h-10 px-4 text-xs">{t('cookie.customize')}</button>
              <button data-testid="cookie-accept" onClick={acceptAll} className="eco-btn-primary !min-h-10 px-4 text-xs">{t('cookie.accept')}</button>
            </div>
          </div>
        </div>
      )}
      {prefsOpen && (
        <div className="fixed inset-0 z-[90] grid place-items-center bg-eco-deep/50 p-4 backdrop-blur-sm" data-testid="cookie-prefs-modal" onClick={() => setPrefsOpen(false)}>
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-eco-ink md:text-lg">{t('cookie.prefs.t')}</h2>
              <button data-testid="cookie-prefs-close" onClick={() => setPrefsOpen(false)} className="text-eco-ink/40 hover:text-eco-ink"><X size={18} /></button>
            </div>
            <div className="mt-4 space-y-3">
              <Toggle id="necessary" label={t('cookie.necessary.t')} desc={t('cookie.necessary.d')} locked />
              <Toggle id="preferences" label={t('cookie.preferences.t')} desc={t('cookie.preferences.d')} />
              <Toggle id="analytics" label={t('cookie.analytics.t')} desc={t('cookie.analytics.d')} />
              <Toggle id="marketing" label={t('cookie.marketing.t')} desc={t('cookie.marketing.d')} />
            </div>
            <p className="mt-3 text-[11px] text-eco-ink/45" data-testid="cookie-marketing-note">{t('cookie.marketing.note')}</p>
            <button data-testid="cookie-prefs-save" onClick={() => save({ preferences: prefs.preferences, analytics: prefs.analytics, marketing: prefs.marketing })} className="eco-btn-primary mt-5 w-full justify-center">{t('cookie.save')}</button>
          </div>
        </div>
      )}
    </>
  );
};
